import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useToast } from "components/atoms/toast/toast-context";
import { GrowthbookFeatures } from "components/growthbook/growthbook.types";
import { constants } from "constants/constants";
import { useBoolean } from "hooks/use-boolean/use-boolean";
import { useGetCurrentPlatform } from "hooks/use-media-query/use-get-currrent-platform";
import jwt_decode from "jwt-decode";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { IApiError } from "services/api/api.types";
import { registerStudent } from "services/api/profiles/profileService";
import { eventNames } from "services/mixpanel/events";
import trackEvent, {
  aliasUser,
  registerSuperProperties,
  setUserProperties
} from "services/mixpanel/mixpanel";
import { useAuth } from "stores/auth/auth-context";
import {
  CountryCode,
  Grade,
  LoginClassType,
  Salutation,
  UserType
} from "types/common/common.types";
import {
  createSuperPropertiesForTrackingEvent,
  getSourceInfoEvent
} from "utils/mixpanel/mixpanel-utils";
import { getDeviceConnectionType, getDeviceMemory } from "utils/tanya-utils";
import {
  addScriptToDocument,
  parseAPIErrorResponse,
  parseStringtoNull
} from "utils/utils";
import { object, mixed, string, InferType } from "yup";
import { StudentFormSchema } from "../use-profile-form.types";
import { IUseLoginStudent } from "./../use-profile-form.types";
import * as Sentry from "@sentry/nextjs";

const FormSchema = object().shape({
  userType: mixed<UserType>().oneOf(Object.values(UserType)).required(),
  name: string().min(2, "Tulis minimal 2 karakter, ya.").required(),
  classType: mixed<LoginClassType>()
    .oneOf(Object.values(LoginClassType))
    .required("Pilih dulu jenjang sekolahmu, ya."),
  grade: string().required("Pilih dulu kelasmu saat ini, ya. "),
  email: string()
    .lowercase()
    .email("Hmm... alamat email ini sepertinya salah.")
    .required(),
  salutation: mixed<Salutation>().oneOf(Object.values(Salutation)).required()
});

const useLoginStudentForm = (
  userType: UserType
): IUseLoginStudent<Omit<StudentFormSchema, "parentName" | "parentNumber">> => {
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const { addToast } = useToast();
  const { user, signOutUser } = useAuth();
  const router = useRouter();
  const { userData, setUserData } = user;

  const [isLoading, setIsLoading] = useBoolean();

  const currentPlatform = useGetCurrentPlatform();

  const useDeviceMemoryCohort = useFeatureIsOn(
    GrowthbookFeatures.identifyDeviceMemoryCohort as string
  );

  const useDeviceConnectionTypeDetection = useFeatureIsOn(
    GrowthbookFeatures.identifyDeviceConnectionType as string
  );

  const decodeJwtTokenFromRegisterToken = () => {
    try {
      if (userData && userData.register_token) {
        const number: { phoneNumber: string } = jwt_decode(
          userData.register_token
        );
        return number.phoneNumber;
      }
    } catch (err) {
      Sentry.captureException(err);
      signOutUser();
    }
  };

  const {
    control,
    handleSubmit,
    formState: { isValid },
    getValues,
    watch
  } = useForm<InferType<typeof FormSchema>>({
    defaultValues: {
      userType: userType,
      salutation: Salutation.Bpk
    },
    mode: "onChange",
    delayError: constants.DELAY_ERROR,
    resolver: yupResolver(FormSchema)
  });

  const userRegisterTrackingData = () => {
    return {
      incomingSource: "website",
      eventSource: "website",
      isOnboarded: false,
      referrer: typeof document !== "undefined" ? document.referrer : null,
      userRole: userData.userType,
      whatsAppConsent: user.isWhatsappConsentGiven
    };
  };

  const triggerTrackingEvents = (
    name: string,
    classType: LoginClassType,
    grade: Grade | string,
    email: string
  ) => {
    trackEvent({
      eventName: eventNames.curriculumSelected,
      payload: {
        ...userRegisterTrackingData()
      }
    });
    trackEvent({
      eventName: eventNames.profileDetailsFilled,
      payload: {
        ...userRegisterTrackingData(),
        name,
        email,
        class: classType,
        grade: grade.split("_")[0]
      }
    });
  };

  const onSubmit: SubmitHandler<
    Omit<StudentFormSchema, "parentName" | "parentNumber">
  > = async ({ name, classType, grade, email }) => {
    try {
      if (userData.register_token) {
        let deviceData = {};
        let memoryData = {};
        let networkData = {};

        setIsLoading.on();
        triggerTrackingEvents(name, classType, grade, email);

        const res = await registerStudent({
          payload: {
            name: name,
            class_type: classType,
            grade: grade.split("_")[0],
            stream: parseStringtoNull(grade.split("_")[1]),
            email: email.toLowerCase(),
            phone_number: decodeJwtTokenFromRegisterToken() || "",
            country_code: CountryCode.Indonesia,
            notification_consent: user.isWhatsappConsentGiven
          },
          token: userData.register_token,
          platform: getSourceInfoEvent(currentPlatform)
        });

        const {
          message,
          data: { student_user }
        } = res;

        if (message === "OK") {
          setUserData({
            ...userData,
            primary_user: student_user,
            register_token: null
          });

          aliasUser(student_user.id);
          registerSuperProperties({
            incomingSource: "website",
            isOnboarded: true,
            userRole: userType,
            whatsAppConsent: user.isWhatsappConsentGiven,
            name: student_user.full_name,
            class: student_user.class_type,
            email: student_user.email,
            grade: student_user.grade,
            isTablet: null,
            id: student_user.id,
            studentId: student_user.student_id,
            stream: student_user.stream,
            onBoardingDate: student_user.onboarded_at,
            mobile: student_user.phone_number
          });
          setUserProperties({
            name: student_user.full_name,
            email: student_user.email,
            id: student_user.id,
            mobile: student_user.phone_number,
            whatsAppConsent: user.isWhatsappConsentGiven,
            onBoardingDate: student_user.onboarded_at,
            isOnboarded: true,
            grade: student_user.grade,
            stream: student_user.stream,
            class: student_user.class_type
          });

          if (useDeviceMemoryCohort) {
            const [memoryCohort, estimatedMemoryRange] = getDeviceMemory();
            memoryData = {
              browserMemoryCohort: memoryCohort,
              estimatedMemoryRange
            };
          }

          if (useDeviceConnectionTypeDetection) {
            const connectionType = getDeviceConnectionType();
            networkData = {
              deviceConnectionType: connectionType
            };
          }

          addScriptToDocument(
            `https://cloud.51degrees.com/api/v4/${process.env.NEXT_PUBLIC_CLOUD_51_CONFIGURATION_TOKEN}.js`,
            {
              onLoad: () => {
                //@ts-ignore
                window.fod?.complete(function (data: {
                  device: {
                    hardwaremodel: string;
                    hardwarename: string[];
                    hardwarevendor: string;
                    isMobile: string;
                    priceband: string;
                    releaseage: string;
                  };
                }) {
                  deviceData = {
                    hardWareModel: data.device.hardwaremodel,
                    hardWareName: data.device.hardwarename[0],
                    hardWareVendor: data.device.hardwarevendor,
                    priceBandInDollars: data.device.priceband,
                    releaseAgeInMonths: data.device.releaseage
                  };

                  trackEvent({
                    eventName: eventNames.studentRegister,
                    payload: {
                      eventSource: "website",
                      sourceInfo: getSourceInfoEvent(currentPlatform),
                      referrer:
                        typeof document !== "undefined"
                          ? document.referrer
                          : null,
                      ...{ ...deviceData, ...memoryData, ...networkData }
                    }
                  });
                });
              },
              onError: () => {
                trackEvent({
                  eventName: eventNames.studentRegister,
                  payload: {
                    eventSource: "website",
                    sourceInfo: getSourceInfoEvent(currentPlatform),
                    referrer:
                      typeof document !== "undefined"
                        ? document.referrer
                        : null,
                    ...memoryData,
                    ...networkData
                  }
                });
              }
            }
          );

          setIsLoading.off();
          router.push({
            pathname: "/user/update-user",
            query: router.query
          });
        }
      }
    } catch (err) {
      Sentry.captureException(err);
      const error = parseAPIErrorResponse(err);
      const { message } = error as IApiError;
      addToast({
        heading: message,
        variant: "error"
      });
      setIsLoading.off();
    }
  };

  useEffect(() => {
    watch();
  });

  useEffect(() => {
    trackEvent({
      eventName: eventNames.createProfileScreenViewed,
      payload: {
        ...createSuperPropertiesForTrackingEvent({ useAuthData: user })
      }
    });
  }, []);

  useEffect(() => {
    const userType = getValues().userType;
    user.setUserData({ ...userData, userType });
  }, [getValues().userType]);

  return {
    control,
    getValues,
    handleSubmit,
    isValid,
    onSubmit,
    isSuccessModalOpen,
    setIsSuccessModalOpen,
    isLoading
  };
};

export { useLoginStudentForm };
